import { useState } from 'react';
import { useRegisterCustomer } from 'hooks';
// import SiteLink from 'components/shared/sitelink';
import Breadcrumbs from 'components/layout/breadcrumbs/breadcrumbs';
import Image from 'components/shared/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import classNames from 'classnames';
import PasswordValidator from 'password-validator';
import Icon from 'components/shared/icon';
import { getSelectedSalonNo, getStorageCustomerSalonNo } from 'lib/shopify';
import { useGlobalModules } from 'contexts';
import styles from './registerSection.module.scss';

const RegisterSection = ({ locale }) => {
  const globalModules = useGlobalModules();

  const {
    firstNameField,
    lastNameField,
    emailField,
    newsletterField,
    passwordField,
    passwordConfirmationField,
    registerButton,
    pageTitle,
    breadcrumbsSection,
    message,
  } = globalModules?.translations?.account?.register || {};

  const {
    passwordDescription,
    fieldRequired,
    passwordsDontMatch,
    invalidEmail,
    thankYou,
    emailSent,
  } = message || {};

  const registerImage = globalModules?.media?.account?.register?.registerImage?.[0] || {};

  const affiliatedSalonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);
  const [registerCustomer, { loading, response, error }] = useRegisterCustomer(locale);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [passwordField1, setPasswordField1] = useState('');
  const [passwordField2, setPasswordField2] = useState('');
  const isMobile = useIsMobile();
  const defaultFieldErrors = {
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    passwordConfirm: false,
  };
  const [fieldErrors, setFieldErrors] = useState(defaultFieldErrors);

  const validateEmail = str => {
    if (!str) return true;
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return regex.test(str);
  };

  const checkFields = () => {
    const newFieldErrors = { ...defaultFieldErrors };
    let hasErrors = false;
    if (!firstName) {
      newFieldErrors.firstName = fieldRequired;
      hasErrors = true;
    }
    if (!lastName) {
      newFieldErrors.lastName = fieldRequired;
      hasErrors = true;
    }
    if (!email) {
      newFieldErrors.email = fieldRequired;
      hasErrors = true;
    }

    if (email && !validateEmail(email)) {
      newFieldErrors.email = invalidEmail;
      hasErrors = true;
    }

    if (!passwordField1) {
      newFieldErrors.password = fieldRequired;
      hasErrors = true;
    }
    if (!passwordField2) {
      newFieldErrors.passwordConfirm = fieldRequired;
      hasErrors = true;
    }

    const schema = new PasswordValidator();
    schema.is().min(8).is().max(100).has().lowercase().has().uppercase();

    if (passwordField1 && !schema.validate(passwordField1)) {
      newFieldErrors.password = passwordDescription;
      hasErrors = true;
    }

    if (passwordField1 !== passwordField2) {
      newFieldErrors.passwordConfirm = passwordsDontMatch;
      hasErrors = true;
    }

    setFieldErrors(newFieldErrors);
    return !hasErrors;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!checkFields()) return;
    await registerCustomer(
      email,
      passwordField1,
      passwordField2,
      firstName,
      lastName,
      affiliatedSalonNo,
      selectedSalonNo
    );
  };

  return (
    <section className="registerWrapper">
      <h1 className={styles.title}>{pageTitle}</h1>
      <div className={styles.container}>
        <div className={styles.formContainer}>
          {(error?.code === 'CUSTOMER_DISABLED' || response) && (
            <div className={styles.mainMessage}>
              <h2>{thankYou || error?.message}</h2>
              {error?.code === 'CUSTOMER_DISABLED' && <p>{emailSent}</p>}
            </div>
          )}
          <form
            className={classNames(styles.form, {
              [styles.invisible]: error?.code === 'CUSTOMER_DISABLED' || response,
            })}
            onSubmit={e => handleSubmit(e)}
          >
            <div className={styles.label}>{firstNameField}*</div>
            <input
              name="firstName"
              type="text"
              onChange={e => setFirstName(e.target.value)}
              placeholder={firstNameField}
              className={classNames(styles.registerInput, {
                [styles.inputError]: fieldErrors.firstName,
              })}
            />
            {fieldErrors.firstName && (
              <div className={styles.errorText}>{fieldErrors.firstName}</div>
            )}

            <div className={styles.label}>{lastNameField}*</div>
            <input
              name="lastName"
              type="text"
              onChange={e => setLastName(e.target.value)}
              placeholder={lastNameField}
              className={classNames(styles.registerInput, {
                [styles.inputError]: fieldErrors.lastName,
              })}
            />
            {fieldErrors.firstName && (
              <div className={styles.errorText}>{fieldErrors.firstName}</div>
            )}

            <div className={styles.label}>{emailField}*</div>
            <input
              name="email"
              type="text"
              onChange={e => setEmail(e.target.value)}
              placeholder={emailField}
              className={classNames(styles.registerInput, {
                [styles.inputError]: fieldErrors.email,
              })}
            />
            {fieldErrors.email && <div className={styles.errorText}>{fieldErrors.email}</div>}

            <div className={styles.label}>{passwordField}*</div>
            <input
              name="password"
              value={passwordField1}
              onChange={e => setPasswordField1(e.target.value)}
              type="password"
              placeholder={passwordField}
              className={classNames(styles.registerInput, {
                [styles.inputError]: fieldErrors.password,
              })}
            />
            {fieldErrors.password && <div className={styles.errorText}>{fieldErrors.password}</div>}

            <div className={styles.label}>{passwordConfirmationField}*</div>
            <input
              name="passwordConfirm"
              value={passwordField2}
              onChange={e => setPasswordField2(e.target.value)}
              type="password"
              placeholder={passwordField}
              className={classNames(styles.registerInput, {
                [styles.inputError]: fieldErrors.passwordConfirm,
              })}
            />
            {fieldErrors.passwordConfirm && (
              <div className={styles.errorText}>{fieldErrors.passwordConfirm}</div>
            )}
            <div className={styles.subscription}>
              <input name="subscription" type="checkbox" />
              <span>{newsletterField}</span>
            </div>

            <button className={styles.submitButton} type="submit" disabled={loading}>
              {loading ? <Icon type="loading" className={styles.loader} /> : registerButton}
            </button>

            {error && (
              <div className={classNames(styles.mainError, 'studio mt1 error')}>
                <span role="img" aria-label="error">
                  ⚠️
                </span>
                : {error?.message}
              </div>
            )}
          </form>
        </div>
        {!isMobile && (
          <Image
            src={registerImage?.image}
            loading="eager"
            className={styles.sideImage}
            layout="fill"
            priority
          />
        )}
      </div>
    </section>
  );
};

export default RegisterSection;
